import { Plan } from '../types'
import { normalizePlan } from './storage'

/**
 * A copy of the last state this browser agreed with the server.
 *
 * Not a source of truth and not an offline store — the server is the record.
 * It exists so that opening the app on a phone with no signal shows the plan
 * rather than an error, and so a reload doesn't blank the screen while the
 * first request is in flight. Anything edited from a cached start is still
 * queued and sent as a normal save.
 *
 * Keyed by user so that two people sharing a device don't see each other's
 * numbers, and cleared on sign-out.
 */

// Left on the app's old name deliberately: it's an address in someone's
// browser, not a label anyone reads. Changing it would orphan every cache
// already written and buy nothing.
const PREFIX = 'cashflow-calendar:cache:v1:'

interface CacheEntry {
  data: Plan
  version: number
  cachedAt: number
}

export function readCache(key: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(PREFIX + key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.version !== 'number') return null
    return {
      data: normalizePlan(parsed.data),
      version: parsed.version,
      cachedAt: typeof parsed.cachedAt === 'number' ? parsed.cachedAt : 0,
    }
  } catch {
    return null
  }
}

export function writeCache(key: string, data: Plan, version: number) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, version, cachedAt: Date.now() }))
  } catch {
    // A full or disabled localStorage costs us the offline fallback and
    // nothing else, so there is nothing worth interrupting the user for.
  }
}

export function clearCache(key: string) {
  try {
    localStorage.removeItem(PREFIX + key)
  } catch {
    // See above.
  }
}

/** Wipes every cached document — used on sign-out, on a shared device. */
export function clearAllCaches() {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(PREFIX)) keys.push(key)
    }
    keys.forEach((k) => localStorage.removeItem(k))
  } catch {
    // See above.
  }
}
